import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../../../firebase';
import { useInquiry } from '../../../hooks/useInquiry';
import { useAuth } from '../../../hooks/useAuth';
import ClientLayout from '../../../components/layout/ClientLayout';
import { CustomerInquiry, InquiryStatus, MessageData } from '../../../types/inquiry';

const InquiryDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { getInquiry, addMessage, markAsRead } = useInquiry();

  const [inquiry, setInquiry] = useState<CustomerInquiry | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [isAddingMessage, setIsAddingMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [downloadingFile, setDownloadingFile] = useState<string | null>(null);

  // 실시간 업데이트 상태
  const [realtimeEnabled, setRealtimeEnabled] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'connecting' | 'disconnected'>('connecting');
  const [lastUpdateTime, setLastUpdateTime] = useState<Date>(new Date());
  const [newResponseCount, setNewResponseCount] = useState(0);
  const [prevMessagesCount, setPrevMessagesCount] = useState(0);

  // URL state로부터 성공 메시지 표시
  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      // state 정리
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // 성공 메시지 자동 숨김
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Firestore 타임스탬프를 Date로 변환하는 헬퍼 함수
  const convertTimestampToDate = useCallback((data: any): CustomerInquiry => {
    return {
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      adminResponse: data.adminResponse ? {
        ...data.adminResponse,
        respondedAt: data.adminResponse.respondedAt?.toDate() || new Date()
      } : undefined,
      messages: data.messages?.map((msg: any) => ({
        ...msg,
        createdAt: msg.createdAt?.toDate() || new Date()
      })) || [],
      lastReadBy: data.lastReadBy ? Object.keys(data.lastReadBy).reduce((acc, uid) => {
        acc[uid] = data.lastReadBy[uid]?.toDate() || new Date();
        return acc;
      }, {} as { [key: string]: Date }) : undefined
    };
  }, []);

  // 비실시간 로딩 함수
  const loadInquiryDetail = useCallback(async () => {
    if (!id) {
      setError('문의 ID가 없습니다.');
      setLoadingDetail(false);
      return;
    }

    if (!user) {
      return;
    }

    try {
      setLoadingDetail(true);
      setError(null);

      const inquiryData = await getInquiry(id);

      if (inquiryData && user) {
        try {
          await markAsRead(id);
        } catch (readError) {
          console.warn('읽음 상태 업데이트 실패:', readError);
        }
      }

      if (!inquiryData) {
        setError('문의를 찾을 수 없습니다.');
        return;
      }

      if (inquiryData.userUid !== user.uid) {
        console.log('권한 확인 실패:', {
          inquiryUserUid: inquiryData.userUid,
          currentUserUid: user.uid
        });
        setError('접근 권한이 없습니다.');
        return;
      }

      setInquiry(inquiryData);
      setPrevMessagesCount(inquiryData.messages?.length || 0);
    } catch (err) {
      console.error('문의 상세 로드 실패:', err);
      setError(err instanceof Error ? err.message : '문의를 불러오는데 실패했습니다.');
      setConnectionStatus('disconnected');
    } finally {
      setLoadingDetail(false);
    }
  }, [id, getInquiry, user, markAsRead]);

  // 실시간 스트림 설정
  useEffect(() => {
    if (!id || !user || !realtimeEnabled) {
      if (id && user) {
        loadInquiryDetail();
      }
      return;
    }

    setConnectionStatus('connecting');

    const unsubscribe = onSnapshot(
      doc(db, 'customer_inquiries', id),
      (doc) => {
        if (doc.exists()) {
          const inquiryData = convertTimestampToDate({
            id: doc.id,
            ...doc.data()
          });

          // 권한 확인
          if (inquiryData.userUid !== user.uid) {
            setError('접근 권한이 없습니다.');
            setConnectionStatus('disconnected');
            return;
          }

          // 새 답변/메시지 감지
          const currentMessagesCount = inquiryData.messages?.length || 0;
          if (prevMessagesCount > 0 && currentMessagesCount > prevMessagesCount) {
            const newCount = currentMessagesCount - prevMessagesCount;
            setNewResponseCount(prev => prev + newCount);
          }

          setInquiry(inquiryData);
          setPrevMessagesCount(currentMessagesCount);
          setConnectionStatus('connected');
          setLastUpdateTime(new Date());
          setLoadingDetail(false);

          // 읽음 처리
          try {
            markAsRead(id);
          } catch (readError) {
            console.warn('읽음 상태 업데이트 실패:', readError);
          }
        } else {
          setError('문의를 찾을 수 없습니다.');
          setConnectionStatus('disconnected');
          setLoadingDetail(false);
        }
      },
      (error) => {
        console.error('실시간 업데이트 실패:', error);
        setError('실시간 업데이트에 실패했습니다.');
        setConnectionStatus('disconnected');
        setLoadingDetail(false);
      }
    );

    return () => unsubscribe();
  }, [id, user, realtimeEnabled, convertTimestampToDate, prevMessagesCount, markAsRead]);

  // 초기 로드 (실시간이 비활성화된 경우)
  useEffect(() => {
    if (id && user && !realtimeEnabled) {
      loadInquiryDetail();
    }
  }, [id, user, realtimeEnabled, loadInquiryDetail]);

  // 연결 상태 모니터링
  useEffect(() => {
    if (error) {
      setConnectionStatus('disconnected');
    }
  }, [error]);

  // 새 응답 알림 클리어
  const clearNewResponseNotification = useCallback(() => {
    setNewResponseCount(0);
  }, []);

  // 실시간 토글
  const toggleRealtime = useCallback(() => {
    setRealtimeEnabled(!realtimeEnabled);
  }, [realtimeEnabled]);

  // 메시지 추가
  const handleAddMessage = async () => {
    if (!inquiry || !newMessage.trim() || !user) return;

    try {
      setIsAddingMessage(true);

      // 사용자 프로필에서 실제 사용자명 가져오기
      let senderName = '사용자';
      try {
        const userProfileRef = doc(db, 'user_profiles', user.uid);
        const userProfileSnap = await getDoc(userProfileRef);

        if (userProfileSnap.exists()) {
          const userData = userProfileSnap.data();
          senderName = userData.name || user.displayName || user.email || '사용자';
        } else {
          senderName = user.displayName || '사용자';
        }
      } catch (profileError) {
        console.warn('사용자 프로필 조회 실패, 기본값 사용:', profileError);
        senderName = user.displayName || '사용자';
      }

      const messageData: MessageData = {
        content: newMessage.trim(),
        senderUid: user.uid,
        senderType: 'user',
        senderName: senderName
      };

      await addMessage(inquiry.id, messageData);

      // 비실시간 모드일 때만 다시 로드
      if (!realtimeEnabled) {
        const updatedInquiry = await getInquiry(inquiry.id);
        if (updatedInquiry) {
          setInquiry(updatedInquiry);
        }
      }

      setNewMessage('');
      setSuccessMessage('메시지가 추가되었습니다.');
    } catch (error) {
      console.error('메시지 추가 실패:', error);
      setError(error instanceof Error ? error.message : '메시지 추가에 실패했습니다.');
    } finally {
      setIsAddingMessage(false);
    }
  };

  // 상태 라벨 변환
  const getStatusLabel = (status: InquiryStatus): string => {
    switch (status) {
      case 'pending': return '답변대기';
      case 'answered': return '답변완료';
      case 'closed': return '처리완료';
      default: return status;
    }
  };

  // 카테고리 라벨 변환
  const getCategoryLabel = (category: string): string => {
    switch (category) {
      case 'general': return '일반문의';
      case 'portville': return '포트빌';
      case 'parking': return '주차장';
      case 'facility': return '시설물';
      case 'account': return '계정';
      default: return category;
    }
  };

  // 상태 색상
  const getStatusColor = (status: InquiryStatus): string => {
    switch (status) {
      case 'pending': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'answered': return 'text-green-600 bg-green-50 border-green-200';
      case 'closed': return 'text-gray-600 bg-gray-50 border-gray-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  // 우선순위는 관리자만 보는 정보이므로 사용자 UI에서 제거

  // 날짜 포맷팅
  const formatDate = (date: Date): string => {
    return new Intl.DateTimeFormat('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Firebase Storage URL에서 실제 파일명 추출 및 디코딩
  const extractFileName = (url: string): string => {
    try {
      // Firebase Storage URL에서 파일 경로 추출
      const urlObj = new URL(url);
      const pathParam = urlObj.searchParams.get('name');

      if (pathParam) {
        // URL 디코딩
        const decodedPath = decodeURIComponent(pathParam);

        // 경로에서 실제 파일명만 추출 (타임스탬프_ 이후 부분)
        const match = decodedPath.match(/\d+_(.+)$/);
        if (match) {
          return match[1];
        }

        // 매칭되지 않으면 전체 경로에서 파일명 추출
        return decodedPath.split('/').pop() || '첨부파일';
      }

      // name 파라미터가 없으면 URL에서 직접 추출
      const pathParts = url.split('/').pop()?.split('?')[0];
      if (pathParts) {
        const decoded = decodeURIComponent(pathParts);
        const match = decoded.match(/\d+_(.+)$/);
        if (match) {
          return match[1];
        }
        return decoded.split('/').pop() || '첨부파일';
      }

      return '첨부파일';
    } catch (error) {
      console.error('파일명 추출 실패:', error);
      return '첨부파일';
    }
  };

  // 첨부파일 다운로드 (보안 개선: URL 노출 방지)
  const handleDownloadAttachment = async (url: string, filename: string) => {
    try {
      setDownloadingFile(filename);
      setError(null);

      // Firebase Storage URL에서 파일 데이터를 fetch로 가져오기
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Blob으로 변환
      const blob = await response.blob();

      // 임시 객체 URL 생성
      const objectUrl = URL.createObjectURL(blob);

      // 다운로드 링크 생성 및 클릭
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // 메모리 정리
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error('파일 다운로드 실패:', error);
      setError('파일 다운로드에 실패했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setDownloadingFile(null);
    }
  };

  if (error) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="text-red-600 text-lg mb-4">오류가 발생했습니다</div>
            <div className="text-gray-600 mb-6">{error}</div>
            <button
              onClick={() => navigate('/inquiry')}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              문의 목록으로 돌아가기
            </button>
          </div>
        </div>
      </ClientLayout>
    );
  }

  if (loadingDetail) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="text-gray-600">문의 정보를 불러오는 중...</span>
          </div>
        </div>
      </ClientLayout>
    );
  }

  if (!inquiry) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="text-gray-600 text-lg mb-6">문의를 찾을 수 없습니다.</div>
            <button
              onClick={() => navigate('/inquiry')}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              문의 목록으로 돌아가기
            </button>
          </div>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="p-6 max-w-4xl mx-auto">
        {/* 성공 메시지 */}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-green-700">{successMessage}</span>
            </div>
            <button
              onClick={() => setSuccessMessage(null)}
              className="text-green-600 hover:text-green-700"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* 헤더 */}
        <div className="mb-8">
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
            <button
              onClick={() => navigate('/inquiry')}
              className="text-blue-600 hover:text-blue-700"
            >
              고객센터
            </button>
            <span>{'>'}</span>
            <span>문의 상세</span>
          </div>

          {/* 실시간 상태 표시 */}
          <div className="flex items-center justify-between mb-4 p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-4">
              {/* 실시간 연결 상태 */}
              <div className="flex items-center">
                <div className={`w-2 h-2 rounded-full mr-2 ${
                  connectionStatus === 'connected' ? 'bg-green-500 animate-pulse' :
                  connectionStatus === 'connecting' ? 'bg-yellow-500 animate-pulse' :
                  'bg-red-500'
                }`}></div>
                <span className={`text-xs font-medium ${
                  connectionStatus === 'connected' ? 'text-green-700' :
                  connectionStatus === 'connecting' ? 'text-yellow-700' :
                  'text-red-700'
                }`}>
                  {connectionStatus === 'connected' ? '실시간 연결' :
                   connectionStatus === 'connecting' ? '연결 중' :
                   '연결 끊어짐'}
                </span>
              </div>

              {/* 새 응답 알림 */}
              {newResponseCount > 0 && (
                <div className="flex items-center">
                  <div className="bg-blue-100 border border-blue-200 rounded px-2 py-1 flex items-center">
                    <svg className="w-3 h-3 text-blue-600 mr-1 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <span className="text-blue-800 font-medium text-xs">새 답변 {newResponseCount}건</span>
                    <button
                      onClick={clearNewResponseNotification}
                      className="ml-1 text-blue-600 hover:text-blue-800"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              )}

              {/* 마지막 업데이트 시간 */}
              <div className="text-xs text-gray-500">
                업데이트: {lastUpdateTime.toLocaleTimeString()}
              </div>
            </div>

            {/* 실시간 토글 버튼 */}
            <button
              onClick={toggleRealtime}
              className={`p-1 rounded transition-colors text-xs ${
                realtimeEnabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
              }`}
              title={realtimeEnabled ? '실시간 업데이트 끄기' : '실시간 업데이트 켜기'}
            >
              {realtimeEnabled ? '실시간 ON' : '실시간 OFF'}
            </button>
          </div>

          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold text-[#1e2c49] mb-2">{inquiry.subject}</h1>
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <span>{formatDate(inquiry.createdAt)}</span>
                <span>작성자: {inquiry.userInfo.name}</span>
                <span className={`px-2 py-1 text-xs font-medium rounded border ${getStatusColor(inquiry.status)}`}>
                  {getStatusLabel(inquiry.status)}
                </span>
              </div>
            </div>
            <button
              onClick={() => navigate('/inquiry')}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              목록으로
            </button>
          </div>
        </div>

        {/* 문의 정보 */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="p-6">
            <div className="grid grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">카테고리</label>
                <p className="text-gray-900">{getCategoryLabel(inquiry.category)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">작성자</label>
                <p className="text-gray-900">{inquiry.userInfo.name}</p>
                <p className="text-sm text-gray-500">{inquiry.userInfo.organization}</p>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">문의 내용</label>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-gray-900 whitespace-pre-wrap">{inquiry.content}</p>
              </div>
            </div>

            {/* 첨부파일 */}
            {inquiry.attachments && inquiry.attachments.length > 0 && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">첨부파일</label>
                <div className="space-y-2">
                  {inquiry.attachments.map((url, index) => {
                    const filename = extractFileName(url);
                    return (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded border">
                        <div className="flex items-center">
                          <svg className="w-5 h-5 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                          </svg>
                          <span className="text-gray-700">{filename}</span>
                        </div>
                        <button
                          onClick={() => handleDownloadAttachment(url, filename)}
                          disabled={downloadingFile === filename}
                          className="text-blue-600 hover:text-blue-700 text-sm font-medium disabled:text-gray-400 disabled:cursor-not-allowed flex items-center gap-1"
                        >
                          {downloadingFile === filename ? (
                            <>
                              <div className="animate-spin rounded-full h-3 w-3 border-b border-blue-600"></div>
                              <span>다운로드 중...</span>
                            </>
                          ) : (
                            '다운로드'
                          )}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 관리자 답변 */}
        {inquiry.adminResponse && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-gray-900">관리자 답변</h3>
                    {(() => {
                      const thirtyMinutesAgo = new Date(Date.now() - 30 * 60 * 1000);
                      const isRecentResponse = inquiry.adminResponse.respondedAt > thirtyMinutesAgo;
                      return isRecentResponse && (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full border border-green-200 animate-pulse">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          NEW
                        </span>
                      );
                    })()}
                  </div>
                  <p className="text-sm text-gray-500">
                    {inquiry.adminResponse.adminName} • {formatDate(inquiry.adminResponse.respondedAt)}
                  </p>
                </div>
              </div>
              <div className={`p-4 bg-blue-50 rounded-lg ${
                (() => {
                  const thirtyMinutesAgo = new Date(Date.now() - 30 * 60 * 1000);
                  const isRecentResponse = inquiry.adminResponse.respondedAt > thirtyMinutesAgo;
                  return isRecentResponse ? 'ring-2 ring-blue-300 animate-fadeIn' : '';
                })()
              }`}>
                <p className="text-gray-900 whitespace-pre-wrap">{inquiry.adminResponse.content}</p>
              </div>
            </div>
          </div>
        )}

        {/* 추가 메시지들 */}
        {inquiry.messages && inquiry.messages.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
            <div className="p-6">
              <h3 className="font-medium text-gray-900 mb-4">대화 내용</h3>
              <div className="space-y-4">
                {inquiry.messages.map((message) => {
                  // 최근 메시지인지 확인 (5분 이내)
                  const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
                  const isRecentMessage = message.createdAt > fiveMinutesAgo;

                  return (
                    <div key={message.id} className={`flex ${message.senderType === 'user' ? 'justify-end' : 'justify-start'} ${
                      isRecentMessage ? 'animate-fadeIn' : ''
                    }`}>
                      <div className={`max-w-xs lg:max-w-md ${message.senderType === 'user' ? 'order-1' : 'order-2'} relative`}>
                        {isRecentMessage && (
                          <div className="absolute -top-2 -left-2">
                            <span className="inline-flex items-center px-1 py-0.5 text-xs font-medium text-green-800 bg-green-100 rounded-full border border-green-200 animate-pulse">
                              NEW
                            </span>
                          </div>
                        )}
                        <div className={`p-3 rounded-lg ${
                          message.senderType === 'user'
                            ? `bg-blue-600 text-white ${isRecentMessage ? 'ring-2 ring-blue-300' : ''}`
                            : `bg-gray-100 text-gray-900 ${isRecentMessage ? 'ring-2 ring-green-300' : ''}`
                        }`}>
                          <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                        </div>
                        <div className={`text-xs text-gray-500 mt-1 ${message.senderType === 'user' ? 'text-right' : 'text-left'}`}>
                          <div className="flex items-center gap-1 ${message.senderType === 'user' ? 'justify-end' : 'justify-start'}">
                            {formatDate(message.createdAt)}
                            {isRecentMessage && (
                              <svg className="w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* 메시지 추가 또는 처리완료 안내 */}
        {inquiry.status === 'closed' ? (
          <div className="bg-gray-50 rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 text-center">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="font-medium text-gray-700 mb-2">처리가 종료된 문의입니다</h3>
              <p className="text-sm text-gray-600">관리자가 문의를 완료 처리하였습니다. 추가 메시지를 남길 수 없습니다.</p>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6">
              <h3 className="font-medium text-gray-900 mb-4">추가 메시지</h3>
              <div className="space-y-4">
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="추가로 전달하고 싶은 내용이 있으시면 작성해주세요..."
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  maxLength={1000}
                />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">{newMessage.length}/1000</span>
                  <button
                    onClick={handleAddMessage}
                    disabled={isAddingMessage || !newMessage.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                  >
                    {isAddingMessage ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        전송 중...
                      </div>
                    ) : (
                      '메시지 보내기'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </ClientLayout>
  );
};

export default InquiryDetailPage;